import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

const BRAND = {
  primary: '#C04C2A',
  secondary: '#EC7347',
  accent: '#F7A35C',
  dark: '#4A1B13',
  soft: '#FFF4EC',
  border: '#D9B6A8',
};

const BRAND_GRADIENT = `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.secondary} 58%, ${BRAND.accent} 100%)`;

const postJson = async (url, data) => {
  try {
    const response = await axios.post(url, data);
    return response;
  } catch (error) {
    return error.response?.data ? { status: error.response.status, ...error.response.data } : { message: 'Network error. Please try again.' };
  }
};

const inputClass = "w-full rounded-xl border bg-white p-3 text-sm text-gray-900 outline-none transition focus:border-[#C04C2A] focus:ring-4";
const inputStyle = { borderColor: BRAND.border, '--tw-ring-color': 'rgba(236, 115, 71, 0.22)' };

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState('email'); // email -> otp -> success
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const response = await postJson(`${config.baseurl}Auth/forgot-password`, { email: email.trim() });
      if (response?.status === 200) {
        setInfo(response.data?.message || response.message || 'A verification code has been sent to your email address.');
        setStep('otp');
      } else {
        setError(response?.message || 'This email address could not be found.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Password and confirm password do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await postJson(`${config.baseurl}Auth/reset-password`, {
        email: email.trim(),
        otp: otp.trim(),
        newPassword,
        confirmPassword,
      });

      if (response?.status === 200) {
        setStep('success');
      } else {
        setError(response?.message || 'Invalid or expired verification code.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="flex min-h-screen items-center justify-center px-4 py-8"
      style={{
        backgroundColor: '#fffaf6',
        backgroundImage: `
          radial-gradient(circle at top left, rgba(247, 163, 92, 0.22), transparent 32%),
          radial-gradient(circle at bottom right, rgba(192, 76, 42, 0.18), transparent 30%),
          linear-gradient(145deg, #fffaf6 0%, #fff2e7 50%, #fde6d9 100%)
        `,
      }}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-3xl border bg-white/95 shadow-[0_25px_70px_rgba(74,27,19,0.16)] backdrop-blur-sm"
        style={{ borderColor: '#f1d3c6' }}
      >
        <div className="h-2 w-full" style={{ background: BRAND_GRADIENT }} />

        <div className="px-6 pt-6">
          <div
            className="inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em]"
            style={{ borderColor: '#f4d7c8', color: '#8B2A15' }}
          >
            BanglaFire CRM
          </div>
        </div>

        <div className="space-y-6 p-6">
          <div>
            <h1 className="text-center text-2xl font-semibold tracking-tight" style={{ color: BRAND.dark }}>
              {step === 'email' && 'Forgot your password?'}
              {step === 'otp' && 'Enter verification code'}
              {step === 'success' && 'Password reset'}
            </h1>
            <p className="mt-1 text-center text-sm text-gray-500">
              {step === 'email' && 'Enter your account email and we will send you a verification code.'}
              {step === 'otp' && `We sent a 6-digit code to ${email}.`}
              {step === 'success' && 'Your password has been changed successfully.'}
            </p>
          </div>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {info && step === 'otp' && (
            <div className="rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
              {info}
            </div>
          )}

          {step === 'email' && (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="name@company.com"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl py-3 text-sm font-semibold text-white shadow-lg transition hover:brightness-105 focus:ring-4 disabled:opacity-60"
                style={{ background: BRAND_GRADIENT, '--tw-ring-color': 'rgba(236, 115, 71, 0.28)', boxShadow: '0 16px 30px rgba(192, 76, 42, 0.22)' }}
              >
                {loading ? 'Sending code...' : 'Send verification code'}
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Verification Code</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                  maxLength={6}
                  placeholder="6-digit code"
                  className={`${inputClass} tracking-[0.5em] text-center font-semibold`}
                  style={inputStyle}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="********"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="********"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl py-3 text-sm font-semibold text-white shadow-lg transition hover:brightness-105 focus:ring-4 disabled:opacity-60"
                style={{ background: BRAND_GRADIENT, '--tw-ring-color': 'rgba(236, 115, 71, 0.28)', boxShadow: '0 16px 30px rgba(192, 76, 42, 0.22)' }}
              >
                {loading ? 'Resetting password...' : 'Reset password'}
              </button>

              <button
                type="button"
                onClick={() => { setStep('email'); setOtp(''); setError(''); setInfo(''); }}
                className="w-full text-center text-sm font-medium transition hover:underline"
                style={{ color: BRAND.primary }}
              >
                Use a different email
              </button>
            </form>
          )}

          {step === 'success' && (
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full rounded-xl py-3 text-sm font-semibold text-white shadow-lg transition hover:brightness-105 focus:ring-4"
              style={{ background: BRAND_GRADIENT, '--tw-ring-color': 'rgba(236, 115, 71, 0.28)', boxShadow: '0 16px 30px rgba(192, 76, 42, 0.22)' }}
            >
              Back to sign in
            </button>
          )}

          {step !== 'success' && (
            <div className="text-center text-sm">
              <a href="/login" className="font-medium transition hover:underline" style={{ color: BRAND.primary }}>
                Back to sign in
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ForgotPassword;
